import { useState } from 'react';
import { api } from '@/lib/api';
import { Button, Input, Textarea } from '@/components/ui';

type FeedbackType = 'bug' | 'feature';

export interface FeedbackFormProps {
  onClose: () => void;
}

export function FeedbackForm({ onClose }: FeedbackFormProps) {
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    if (!trimmedTitle || !trimmedDescription) {
      setError('Please enter a title and description.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: apiError } = await api.post<{ ok: boolean }>('/feedback', {
        type,
        title: trimmedTitle,
        description: trimmedDescription,
        page_url: window.location.href,
      });

      if (apiError) {
        setError(apiError);
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Failed to submit feedback. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-4 p-6 text-center">
        <p className="text-lg font-medium text-slate-900 dark:text-white">
          Thanks, your feedback was submitted.
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Our team will review your report.
        </p>
        <Button onClick={onClose} className="mx-auto">
          Close
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
        Submit feedback
      </h2>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setType('bug')}
          className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
            type === 'bug'
              ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800'
          }`}
        >
          Bug report
        </button>
        <button
          type="button"
          onClick={() => setType('feature')}
          className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
            type === 'feature'
              ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800'
          }`}
        >
          Feature request
        </button>
      </div>

      <Input
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Brief summary"
        maxLength={120}
        required
      />

      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What happened or what would you like to see?"
        rows={6}
        maxLength={8000}
        required
      />

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isSubmitting}>
          Submit
        </Button>
      </div>
    </form>
  );
}
